/* ============================================================
   Pictorial questionnaire.

   Many workers on site cannot read the survey text — it arrives from CarePlix in
   clinical English regardless of the selected language. An unreadable question
   does not get skipped; it gets answered at random, and that random answer feeds
   the lung score. So it is worth showing a picture and plain words.

   IMPORTANT: this changes only what is DISPLAYED. The option `value` sent to
   `submit-survey` is always the untouched value CarePlix supplied — we never
   invent, reorder or re-map the payload. If a question is not recognised the
   original text and original option labels are shown unchanged, so an
   unrecognised question is merely plain, never wrong.
   ============================================================ */

/* Icons are inline SVG rather than emoji: emoji rendering varies wildly across
   Android builds and several of these would fall back to a blank box. */
const ICONS = {
  /* Coughing face with droplets and speech burst, per the supplied reference. */
  cough: `<path d="M15 3c-1.2 1.6-1.6 3.2-1 4.6"/><path d="M17 5h2M18.5 3.5l1.5-1M19 8h2"/>
    <path d="M14 8c-1.6 1.2-2.2 2.6-2 4 .2 1.2-.6 1.6-1.4 1.8"/>
    <path d="M11 15.5c1.6-.4 3.2-.3 4.6.2"/><path d="M12 18.5c1.4-.6 2.6-.7 3.8-.3"/>
    <path d="M7.5 13.5c.6.9.9 1.5.9 2a.9.9 0 0 1-1.8 0c0-.5.3-1.1.9-2z"/>
    <path d="M9 17.5c.8 1.2 1.2 2 1.2 2.6a1.2 1.2 0 0 1-2.4 0c0-.6.4-1.4 1.2-2.6z"/>
    <circle cx="6" cy="6.5" r="4.2"/><path d="M4.4 5.4h1.2M7.6 6.8h.6M5 8.4h.5"/>`,
  /* Cigarette with rising smoke. */
  smoke: `<path d="M3 17.5h11v2.5H3z"/><path d="M16 17.5h1.2v2.5H16zM19 17.5h1.2v2.5H19z"/>
    <path d="M14 15c0-2 2.6-2 2.6-4S14.4 8.6 14.4 6.6c0-1.4 1.2-2.4 2.6-2.4"/>
    <path d="M18 15c0-1.6 1.8-1.8 1.8-3.4S18 10 18 8.4"/>`,
  /* Lungs with sound waves — wheeze / breathlessness. */
  breath: `<path d="M12 12V5"/>
    <path d="M12 12c0-3.4-3.4-5.6-5.6-4.4C4.6 8.6 4.6 13 5.6 16c.6 1.8 2.2 2.4 4 1.8 1.6-.5 2.4-2 2.4-3.6z"/>
    <path d="M12 12c0-3.4 3.4-5.6 5.6-4.4 1.8 1 1.8 5.4.8 8.4-.6 1.8-2.2 2.4-4 1.8-1.6-.5-2.4-2-2.4-3.6z"/>`,
  wheeze: `<path d="M12 12V5"/>
    <path d="M12 12c0-3.4-3.4-5.6-5.6-4.4C4.6 8.6 4.6 13 5.6 16c.6 1.8 2.2 2.4 4 1.8 1.6-.5 2.4-2 2.4-3.6z"/>
    <path d="M12 12c0-3.4 3.4-5.6 5.6-4.4 1.8 1 1.8 5.4.8 8.4-.6 1.8-2.2 2.4-4 1.8-1.6-.5-2.4-2-2.4-3.6z"/>
    <path d="M19.5 9.5a4 4 0 0 1 0 5M21.6 8a6.4 6.4 0 0 1 0 8"/>`,
  chest: `<path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z"/>`,
  fever: `<path d="M14 14.8V4a2 2 0 0 0-4 0v10.8a4 4 0 1 0 4 0z"/><path d="M18 6h3M18 10h3"/>`,
  sputum: `<path d="M12 3s5 6 5 10a5 5 0 0 1-10 0c0-4 5-10 5-10z"/>`,
  night: `<path d="M20 14a8 8 0 1 1-9.9-9.9A7 7 0 0 0 20 14z"/>`,
  /* Figure climbing stairs, per the supplied reference. */
  stairs: `<path d="M2.5 21h4.2v-3.4h3.4v-3.4h3.4V10.8h3.4V7.4h4.6" stroke-width="2"/>
    <circle cx="8.4" cy="4.2" r="2"/>
    <path d="M8.4 6.6v4.2l-2.6 4M8.4 8.6l2.8 2.2.2 3.4M8.4 8.2 6 9.6M8.4 8.2l3.2 1.2 1.8-.4"/>`,
  dust: `<circle cx="7" cy="9" r="1.4"/><circle cx="13" cy="6" r="1.1"/><circle cx="17" cy="11" r="1.4"/><circle cx="10" cy="14" r="1.1"/><circle cx="15" cy="17" r="1.2"/>`,
  person: `<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6"/>`,
  question: `<circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7v.5"/><path d="M12 17.5h.01"/>`,
};

/** Question patterns → icon and plain-language prompt. */
const QUESTION_MAP = [
  { re: /smok|tobacco|cigarette|beedi|bidi/i, icon: "smoke", plain: "Do you smoke?" },
  { re: /night|lying down|sleep/i, icon: "night", plain: "Does it get worse at night?" },
  { re: /stair|climb|walk|exert|activity/i, icon: "stairs", plain: "Breathless climbing stairs?" },
  { re: /wheez|whistl/i, icon: "wheeze", plain: "Whistling sound when breathing?" },
  { re: /phlegm|sputum|mucus|spit/i, icon: "sputum", plain: "Bringing up phlegm?" },
  { re: /chest.*(pain|tight)|tight.*chest/i, icon: "chest", plain: "Chest tight or painful?" },
  { re: /fever|temperature/i, icon: "fever", plain: "Fever recently?" },
  { re: /dust|smoke exposure|fume|pollution|chemical/i, icon: "dust", plain: "Work around dust or fumes?" },
  { re: /breath|dyspn|short of breath/i, icon: "breath", plain: "Short of breath?" },
  { re: /cough/i, icon: "cough", plain: "Do you have a cough?" },
  { re: /gender|sex\b/i, icon: "person", plain: "Are you male or female?" },
];

/** Option label patterns → plain word and a tone for colouring. */
const OPTION_MAP = [
  { re: /^(yes|y)$/i, plain: "Yes", tone: "yes" },
  { re: /never/i, plain: "Never", tone: "no" },
  { re: /^(no|n|none|nil)$/i, plain: "No", tone: "no" },
  { re: /sometimes|occasional|now and then|rarely|mild/i, plain: "Sometimes", tone: "maybe" },
  { re: /often|frequent|most days|moderate/i, plain: "Often", tone: "yes" },
  { re: /always|daily|every day|severe|a lot/i, plain: "Always", tone: "yes" },
  { re: /^male$|^m$/i, plain: "Male", tone: "neutral" },
  { re: /^female$|^f$/i, plain: "Female", tone: "neutral" },
  { re: /don'?t know|not sure|unsure/i, plain: "Not sure", tone: "maybe" },
];

export function iconPath(name) {
  return ICONS[name] || ICONS.question;
}

/**
 * Decorate a question for display. Never alters ids or values.
 * @returns {{icon:string, plain:string|null, options:Array}}
 */
export function decorate(q) {
  const text = String(q.text || "");
  const hit = QUESTION_MAP.find((m) => m.re.test(text));

  let options = (q.options || []).map((o) => {
    const label = String(o.label ?? "");
    const om = OPTION_MAP.find((m) => m.re.test(label.trim()));
    return {
      ...o,                       // value is carried through untouched
      plain: om ? om.plain : label,
      tone: om ? om.tone : "neutral",
      original: label,
    };
  });

  // Simplification must never make two different answers look the same
  // (e.g. "Occasionally" and "Sometimes" both becoming "Sometimes"). If any
  // plain words collide, show every option with its original label instead —
  // the full scale the survey intended.
  const plains = options.map((o) => o.plain.trim().toLowerCase());
  if (new Set(plains).size !== plains.length) {
    options = options.map((o) => ({ ...o, plain: o.original }));
  }

  return {
    icon: hit ? hit.icon : "question",
    // Only replace the wording when we actually recognised the question; a
    // mis-simplified clinical question is worse than the original.
    plain: hit ? hit.plain : null,
    options,
  };
}
